<template>
  <div 
    class="datepicker__input"
    @click="toggleDatepicker"
    @keyup.enter.stop.prevent="toggleDatepicker"
    data-qa="datepickerInput"
    :class="inputClass"
    :tabindex="tabIndex"
  >
    {{ inputDate ? inputDate : i18n[inputDateType] }}
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  inputDate: {
    type: String,
    default: null,
  },
  inputDateType: {
    type: String,
    default: 'check-in'
  },
  singleDaySelection: {
    type: Boolean,
    default: false,
  },
  toggleDatepicker: {
    type: Function,
    required: true,
  },
  showDatepicker: {
    type: Function,
    required: true,
  },
  i18n: {
    type: Object,
    required: true,
  },
})

// Computed
const inputClass = computed(() => ({
  'datepicker__input--is-active': props.isOpen && props.inputDate == null,
  'datepicker__input--single-date': props.singleDaySelection,
}))

const tabIndex = computed(() => 
  props.inputDateType === 'check-in' ? 0 : -1
)
</script>

<style lang="scss" scoped>
/* =============================================================
     * VARIABLES
     * ============================================================*/
$white: #fff;
$black: #000;
$gray: #424b53;
$primary-text-color: #4A3E3E;
$lightest-gray: #f3f5f8;
$primary-color: #4A3E3E;
$primary-color: $primary-color;
$medium-gray: #999999;
$light-gray: #707070;
$dark-gray: #2d3047;

$font-small: 14px;

/* =============================================================
     * RESPONSIVE LAYOUT HELPERS
     * ============================================================*/
$tablet: "(min-width: 480px) and (max-width: 767px)";
$phone: "(max-width: 479px)";
$desktop: "(min-width: 768px)";
$up-to-tablet: "(max-width: 767px)";
$extra-small-screen: "(max-width: 23em)";

@mixin focusStyle() {
}

@mixin device($device-widths) {
  @media screen and ($device-widths) {
    @content;
  }
}

.square {
  width: calc(100% / 7);
  float: left;
  @include device($desktop) {
    cursor: pointer;
  }
}
.datepicker__wrapper {
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

/* =============================================================
     * BASE STYLES
     * ============================================================*/

.datepicker {
  transition: all 0.2s ease-in-out;
  background-color: $white;
  color: $gray;
  font-size: 16px;
  line-height: 14px;
  overflow: hidden;
  left: 0;
  top: 48px;
  position: absolute;
  z-index: 999;
  width: 690px;

  button.next--mobile {
    background: none;
    border: 1px solid $light-gray;
    float: none;
    height: 50px;
    width: 100%;
    position: relative;
    background-position: center;
    appearance: none;
    overflow: hidden;
    position: fixed;
    bottom: 0;
    left: 0;
    outline: none;
    box-shadow: 0 5px 30px 10px rgba($black, 0.08);
    background: white;

    &:after {
      background: transparent url("/ic-arrow-right-green.regular.svg") no-repeat
        center / 8px;
      transform: rotate(90deg);
      content: "";
      position: absolute;
      width: 200%;
      height: 200%;
      top: -50%;
      left: -50%;
    }
  }

  &--closed {
    box-shadow: 0 15px 30px 10px rgba($black, 0);
    max-height: 0;
  }

  &--open {
    box-shadow: 0 15px 30px 10px rgba($black, 0.08);
    max-height: 900px;

    @include device($up-to-tablet) {
      box-shadow: none;
      height: 100%;
      left: 0;
      right: 0;
      bottom: 0;
      -webkit-overflow-scrolling: touch !important;
      position: fixed;
      top: 0;
      width: 100%;
    }
  }

  &__wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
    height: 48px;
    z-index: 90;
  }

  &__dummy-wrapper {
    border: solid 1px $light-gray;
    cursor: pointer;
    display: block;
    float: left;
    width: 100%;
    height: 100%;

    &--no-border.datepicker__dummy-wrapper {
      margin-top: 15px;
      border: 0;
    }

    &--is-active {
      border: 1px solid #707070;
    }
  }


  &__month-day {
    visibility: visible;
    text-align: center;
    margin: 0;
    border: 0;
    height: 40px;
    padding-top: 14px;

    @include focusStyle();

    &--invalid-range {
      background-color: rgba($primary-color, 0.3);
      color: $lightest-gray;
      cursor: not-allowed;
      position: relative;
    }

    &--invalid {
      color: $lightest-gray;
      cursor: not-allowed;
    }

    &--valid:hover,
    &--allowed-checkout:hover {
      background-color: $white;
      color: $primary-color;
      z-index: 1;
      position: relative;
      box-shadow: 0 0 10px 3px rgba($gray, 0.4);
    }

    &--disabled {
      opacity: 0.25;
      cursor: not-allowed;
      pointer-events: none;
      position: relative;
    }

    &--selected {
      background-color: rgba($primary-color, 0.5);
      color: $white;

      &:hover {
        background-color: $white;
        color: $primary-color;
        z-index: 1;
        position: relative;
        box-shadow: 0 0 10px 3px rgba($gray, 0.4);
      }
    }

    &--first-day-selected,
    &--last-day-selected {
      background: $primary-color;
      color: $white;
    }

    &--allowed-checkout {
      color: $medium-gray;
    }

    &--out-of-range {
      color: $lightest-gray;
      cursor: not-allowed;
      position: relative;
      pointer-events: none;
    }

    &--valid {
      cursor: pointer;
      color: $medium-gray;
    }

    &--hidden {
      opacity: 0.25;
      pointer-events: none;
      color: $white;
    }
  }

  &__month-button {
    background: transparent url("/ic-arrow-right-datepicker.regular.svg") no-repeat
      center center / 8px;
    cursor: pointer;
    display: inline-block;
    height: 60px;
    width: 60px;

    @include focusStyle();

    &--prev {
      transform: rotateY(180deg);
    }

    &--next {
      float: right;
    }

    &--locked {
      opacity: 0.2;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &__inner {
    padding: 20px;
    float: left;

    @include device($up-to-tablet) {
      padding: 0;
    }
  }

  &__months {
    @include device($desktop) {
      width: 650px;
    }

    @include device($up-to-tablet) {
      margin-top: 92px;
      height: calc(100% - 92px);
      position: absolute;
      left: 0;
      top: 0;
      overflow: scroll;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }

    &::before {
      background: $light-gray;
      bottom: 0;
      content: "";
      display: block;
      left: 50%;
      position: absolute;
      top: 0;
      width: 1px;

      @include device($up-to-tablet) {
        display: none;
      }
    }
  }

  &__month {
    font-size: 12px;
    float: left;
    width: 50%;
    padding-right: 10px;

    @include device($up-to-tablet) {
      width: 100%;
      padding-right: 0;
      padding-top: 60px;

      &:last-of-type {
        margin-bottom: 65px;
      }
    }

    @include device($desktop) {
      &:last-of-type {
        padding-right: 0;
        padding-left: 10px;
      }
    }
  }

  &__month-caption {
    height: 2.5em;
    vertical-align: middle;
  }

  &__month-name {
    font-size: 16px;
    font-weight: 500;
    margin-top: -40px;
    padding-bottom: 17px;
    pointer-events: none;
    text-align: center;

    @include device($up-to-tablet) {
      margin-top: -25px;
      margin-bottom: 0;
      position: absolute;
      width: 100%;
    }
  }

  &__week-days {
    height: 2em;
    vertical-align: middle;
  }

  &__week-row {
    border-bottom: 5px solid $white;
    height: 38px;

    @include device($up-to-tablet) {
      box-shadow: 0 13px 18px -8px rgba($black, 0.07);
      height: 25px;
      left: 0;
      top: 65px;
      position: absolute;
      width: 100%;
    }
  }

  &__week-name {
    width: calc(100% / 7);
    float: left;
    font-size: 12px;
    font-weight: 400;
    color: $medium-gray;
    text-align: center;
  }

  &__close-button {
    appearance: none;
    background: transparent;
    border: 0;
    color: $primary-color;
    cursor: pointer;
    font-size: 21px;
    font-weight: bold;
    margin-top: 0;
    outline: 0;
    z-index: 10000;
    position: fixed;
    left: 7px;
    top: 5px;
    transform: rotate(45deg);
  }

  &__clear-button {
    appearance: none;
    background: transparent;
    border: 0;
    cursor: pointer;
    font-size: 25px;
    font-weight: bold;
    height: 40px;
    margin-bottom: 0;
    margin-left: 0;
    margin-right: -2px;
    margin-top: 4px;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 40px;

    svg {
      fill: none;
      stroke-linecap: round;
      stroke-width: 8px;
      stroke: $medium-gray;
      width: 20px;
      width: 14px;
      top: -3px;
      position: relative;
    }

    @include focusStyle();
  }

  &__tooltip {
    background-color: $dark-gray;
    border-radius: 2px;
    color: $white;
    font-size: 11px;
    margin-left: 5px;
    margin-top: -22px;
    padding: 5px 10px;
    position: absolute;
    z-index: 50;

    &:after {
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid $dark-gray;
      bottom: -4px;
      content: "";
      left: 50%;
      margin-left: -4px;
      position: absolute;
    }
  }
}

// Modifiers

.overflow-hidden {
  overflow: hidden;
}

.is-hidden {
  display: none;
}

.hide-up-to-tablet {
  @include device($up-to-tablet) {
    display: none;
  }
}

.hide-on-desktop {
  display: block;
}
@media (min-width: 768px) {
  .hide-on-desktop {
    display: none;
  }
}
</style>
